import React from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  Divider,
  LinearProgress,
  Chip
} from '@mui/material';
// import DarkModeToggle from '../shared/DarkModeToggle';
import { 
  Dashboard as DashboardIcon,
  Save as SaveIcon,
  Logout as LogoutIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckIcon,
  Menu as MenuIcon,
  Brightness4,
  Brightness7
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { signOutUserAsync } from '../../store/authSlice';
import { useTheme } from '../../contexts/ThemeContext';

interface NavBarProps {
  onSaveDraft?: () => void;
  showSaveDraft?: boolean;
  isSaving?: boolean;
  progress?: number;
  showProgress?: boolean;
}

const NavBar: React.FC<NavBarProps> = ({ onSaveDraft, showSaveDraft = false, isSaving = false, progress = 0, showProgress = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { user, userProfile } = useSelector((state: RootState) => state.auth);
  const { mode, toggleTheme } = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  
  // Environment variable to control mobile progress visibility
  const hideMobileProgress = process.env.REACT_APP_HIDE_MOBILE_PROGRESS === 'true';

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await dispatch(signOutUserAsync()).unwrap();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleDashboard = () => {
    handleClose();
    navigate('/dashboard');
  };

  const handleNewLease = () => {
    handleClose();
    navigate('/create-lease');
  };

  const getInitials = () => {
    if (userProfile?.firstName && userProfile?.lastName) {
      return `${userProfile.firstName[0]}${userProfile.lastName[0]}`.toUpperCase();
    }
    if (user?.displayName) {
      const parts = user.displayName.split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return user.displayName[0].toUpperCase();
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  return (
    <>
      <AppBar 
        position="sticky" 
        sx={{ 
          background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', px: { xs: 0, md: 2 } }}>
        <Box sx={{ display: 'flex', flexShrink: 0 }}>
                  {/* Logo */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            mr: 3
          }}
          onClick={() => navigate('/dashboard')}
        >
          <img 
            src="/TenantTermWhite.png" 
            alt="TenantTerms logo" 
            style={{ height: 40, marginRight: 10 }}
          />
        </Box>

        {/* Navigation Links */}
        <Box sx={{ display: 'flex', gap: 2, flexShrink: 0 }}>
          <Button
            color="inherit"
            startIcon={<DashboardIcon />}
            onClick={() => navigate('/dashboard')}
            sx={{
              textTransform: 'none',
              fontWeight: location.pathname === '/dashboard' ? 700 : 400,
              opacity: location.pathname === '/dashboard' ? 1 : 0.8,
              '&:hover': { opacity: 1 },
              display: { xs: 'none', md: 'flex' }
            }}
          >
            Dashboard
          </Button>
          
        </Box>
        </Box>
        <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'right', alignItems: 'center', gap: 1 }}>
                  {location.pathname === '/dashboard' && (
            <Button
              color="inherit"
              startIcon={<AddIcon />}
              onClick={() => navigate('/create-lease')}
              sx={{
                textTransform: 'none',
                opacity: 0.8,
                '&:hover': { opacity: 1 }
              }}
            >
              New Lease
            </Button>
          )}

        <Box sx={{ display: 'flex', height: 'auto', justifyContent: 'center', alignItems: 'center' }}>
        {/* Save Draft Button */}{/* Save Draft Button */}
        {showSaveDraft && (
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<SaveIcon />}
            onClick={onSaveDraft}
            disabled={isSaving}
            sx={{
              mr: 2,
              borderColor: 'rgba(255,255,255,0.3)',
              '&:hover': {
                borderColor: 'rgba(255,255,255,0.5)',
                background: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        )}
        </Box>

      

        {/* User Menu */}
        <Box>
          <Tooltip title="Account menu">
            <IconButton
              size="large"
              aria-label="menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              elevation: 3,
              sx: {
                mt: 1.5,
                minWidth: 200,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1.5,
                },
              },
            }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {userProfile?.firstName} {userProfile?.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={handleDashboard}>
              <DashboardIcon sx={{ mr: 2, fontSize: 20 }} />
              Dashboard
            </MenuItem>
            <MenuItem onClick={handleNewLease}>
              <AddIcon sx={{ mr: 2, fontSize: 20 }} />
              New Lease
            </MenuItem>
            <MenuItem onClick={() => { handleClose(); navigate('/settings'); }}>
              <SettingsIcon sx={{ mr: 2, fontSize: 20 }} />
              Settings
            </MenuItem>
            <MenuItem onClick={() => { toggleTheme(); handleClose(); }}>
              {mode === 'light' ? (
                <Brightness4 sx={{ mr: 2, fontSize: 20 }} />
              ) : (
                <Brightness7 sx={{ mr: 2, fontSize: 20 }} />
              )}
              {mode === 'light' ? 'Dark Mode' : 'Light Mode'}
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 2, fontSize: 20 }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
        </Box>
      </Toolbar>
    </AppBar>
    
    {/* Progress Bar - Positioned under navbar */}
    {showProgress && !hideMobileProgress && (
      <Box sx={{ 
        position: 'sticky',
        top: { xs: '56px', md: '64px' }, // Mobile navbar is shorter
        zIndex: 1100,
        background: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        px: { xs: 2, md: 2 },
        py: 1,
        display: { xs: 'block', md: 'none' }, // Only show on mobile
        mt: 0 // Remove any top margin
      }}>
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center', 
          gap: 2,
          maxWidth: '1200px',
          mx: 'auto'
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                backgroundColor: 'rgba(0,0,0,0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  background: progress === 100 
                    ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)' // Green when complete
                    : progress >= 85
                    ? 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)' // Light green when very close
                    : progress >= 70
                    ? 'linear-gradient(90deg, #84cc16 0%, #65a30d 100%)' // Lime when close
                    : progress >= 55
                    ? 'linear-gradient(90deg, #eab308 0%, #ca8a04 100%)' // Yellow when getting close
                    : progress >= 40
                    ? 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)' // Orange when halfway
                    : progress >= 25
                    ? 'linear-gradient(90deg, #f97316 0%, #ea580c 100%)' // Light orange when getting started
                    : progress >= 10
                    ? 'linear-gradient(90deg, #fb923c 0%, #f97316 100%)' // Light orange for early progress
                    : 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)' // Yellow for very early progress
                }
              }} 
            />
          </Box>
          <Chip 
            icon={progress === 100 ? <CheckIcon /> : undefined}
            label={`${Math.round(progress)}%`}
            size="small"
            sx={{ 
              bgcolor: progress === 100 
                ? '#10b981' // Green when complete
                : progress >= 85
                ? '#22c55e' // Light green when very close
                : progress >= 70
                ? '#84cc16' // Lime when close
                : progress >= 55
                ? '#eab308' // Yellow when getting close
                : progress >= 40
                ? '#f59e0b' // Orange when halfway
                : progress >= 25
                ? '#f97316' // Light orange when getting started
                : progress >= 10
                ? '#fb923c' // Light orange for early progress
                : '#fbbf24', // Yellow for very early progress
              color: 'white',
              fontWeight: 600,
              flexShrink: 0,
              '& .MuiChip-icon': {
                color: 'white'
              }
            }}
          />
        </Box>
      </Box>
    )}
  </>
  );
};

export default NavBar; 